import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Info, Search } from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import InputCst from "@components/InputCst/InputCst";
import TabsCst from "@components/TabsCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { mapAggregatedType } from "@pages/Contents/V2/Corporate/Catalog/types";
import {
  DetailAccordion,
  SectionHeader,
  searchBoxSx,
} from "../components/detailLayout";
import { useRuleDataset } from "../DynamicGroupWizard/useRuleDataset";
import {
  DynRule,
  DynRuleSet,
  parseRuleOperator,
} from "../DynamicGroupWizard/types";

const PAGE_SIZE = 10;

const LO_TYPE_BY_TAB: Record<string, string> = {
  activities: "activity",
  courses: "course",
  paths: "path",
};

const RulesSummary: React.FC<{ sets: DynRuleSet[] }> = ({ sets }) => {
  const { t } = useTranslation("group-wizard-v2");
  const { t: tDetail } = useTranslation("group-detail-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const { fieldOptions, conditionOptions } = useRuleDataset(corporateId);

  const labelOf = (
    options: { value: string; label: string }[],
    value: string,
  ) => options.find((o) => o.value === value)?.label || value;

  if (!sets || sets.length === 0 || sets.every((s) => !s.rules?.length)) {
    return (
      <Typography sx={{ color: theme.palette.text.tertiary }}>
        {tDetail("rules.empty")}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {sets.map((set, si) => {
        const setLogic = parseRuleOperator(set.operator || "AND").logic;
        return (
          <Box
            key={si}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
              {t("rules.ruleSet")} {si + 1} ·{" "}
              {setLogic === "OR" ? t("rules.any") : t("rules.all")}
            </Typography>
            {(set.rules || []).map((rule: DynRule, ri: number) => {
              const { exclude } = parseRuleOperator(rule.operator || "AND");
              return (
                <Box
                  key={ri}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    size="small"
                    label={exclude ? t("rules.exclude") : t("rules.include")}
                    color={exclude ? "error" : "success"}
                    variant="outlined"
                  />
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {labelOf(fieldOptions, rule.condition_field)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.text.tertiary,
                    }}
                  >
                    {labelOf(conditionOptions, rule.condition)}
                  </Typography>
                  {rule.value && (
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                      {rule.value}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

const DynamicGroupDetail: React.FC = () => {
  const { t } = useTranslation("group-detail-v2");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", groupId = "" } = useParams();

  const dynamicGroupsUrl = ServicesURL.dynamicGroups.replace(
    ":idCorporate",
    corporateId,
  );
  const { dataDetail: groupDetail } = useFetchDetail(
    dynamicGroupsUrl,
    groupId,
    PagesID["corporate.groups-dynamic.edit"],
  );

  // Iniziative associate
  const initiativesUrl = `${ServicesURL.getEnrollmentMassive}?corporateId=${corporateId}&groupId=${groupId}`;
  const { items: initiatives, isLoading: loadingInit } = usePaginatedList(
    initiativesUrl,
    { page: 1, pageSize: PAGE_SIZE },
  );

  // Utenti del gruppo dinamico
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState("");
  const usersUrl = ServicesURL.getDynamicGroupsUsers
    .replace(":idCorporate", corporateId)
    .replace("::groupId", groupId);
  const {
    items: users,
    totalItems: usersTotal,
    totalPages: usersPages,
    isLoading: loadingUsers,
  } = usePaginatedList(
    usersUrl,
    { page: usersPage, pageSize: PAGE_SIZE, search: usersSearch || undefined },
    { responseKey: "response" },
  );

  // Oggetti formativi associati
  const [loTab, setLoTab] = useState("activities");
  const [loPage, setLoPage] = useState(1);
  const [loSearch, setLoSearch] = useState("");
  const loUrl = `${ServicesURL.getLOEnrolledWithEnrollInfo
    .replace(":corporateId", corporateId)
    .replace(":groupId", groupId)}?isStandalone=true`;
  const {
    items: los,
    totalItems: loTotal,
    totalPages: loPages,
    isLoading: loadingLo,
  } = usePaginatedList(loUrl, {
    page: loPage,
    pageSize: PAGE_SIZE,
    search: loSearch || undefined,
  });

  const filteredLos = useMemo(() => {
    const wanted = LO_TYPE_BY_TAB[loTab];
    if (!wanted) return los;
    return los.filter((raw: any) => {
      const type = mapAggregatedType(raw.type || raw.lo?.type);
      return type === wanted;
    });
  }, [los, loTab]);

  const loTabs = [
    { key: "activities", label: t("lo.tabs.activities") },
    { key: "courses", label: t("lo.tabs.courses") },
    { key: "paths", label: t("lo.tabs.paths") },
  ];

  const goToUserDetail = (u: any) =>
    navigate(
      `/v2/corporate/${corporateId}/detail/users-groups/${u.id ?? u.user_id}`,
    );

  return (
    <AnimationFadeIn>
      <Box
        sx={{
          py: 3,
          pr: 3,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {/* Header */}
        <Box>
          <Breadcrumb />
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.tertiary, mt: 1 }}
          >
            ID: {groupId}
          </Typography>
          <Typography sx={{ fontSize: "28px", fontWeight: 700 }}>
            {groupDetail?.name || "-"}
          </Typography>
        </Box>

        {/* Logiche di condizioni e regole */}
        <DetailAccordion title={t("rules.title")}>
          <RulesSummary sets={groupDetail?.sets || []} />
        </DetailAccordion>

        {/* Iniziative associate */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("initiatives.title")}
            // TODO
            // ripristinare l'action quando l'endpoint sarà disponibile
            // action={
            //   <Button
            //     variant="contained"
            //     startIcon={<Plus size={16} />}
            //     sx={{
            //       textTransform: "none",
            //       borderRadius: "8px",
            //       fontWeight: 700,
            //     }}
            //   >
            //     {t("initiatives.associate")}
            //   </Button>
            // }
          />
          {loadingInit ? (
            <CircularProgress />
          ) : initiatives.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("initiatives.empty")}
            </Typography>
          ) : (
            initiatives.map((row: any, index: number) => (
              <ListItemCardCst
                key={row.id ?? index}
                title={row.name || row.initiativeName || t("initiatives.item")}
                subtitle={{ id: row.id ?? row.enrollmentId }}
                badges={[]}
                actions={[]}
              />
            ))
          )}
        </Box>

        {/* Utenti */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("users.title")}
            action={
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                }}
              >
                {t("downloadCsv")}
              </Button>
            }
          />

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: "8px",
              p: 2,
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Info size={20} color={theme.palette.primary.main} />
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                {t("users.dynamicNoticeTitle")}
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: theme.palette.text.tertiary }}
              >
                {t("users.dynamicNoticeText")}
              </Typography>
            </Box>
          </Box>

          <Box sx={searchBoxSx}>
            <InputCst
              id="dyn-group-users-search"
              label={t("search")}
              value={usersSearch}
              onChange={(e: any) => {
                setUsersSearch(e.target.value);
                setUsersPage(1);
              }}
              endAdornment={
                <Search size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>

          {loadingUsers ? (
            <CircularProgress />
          ) : users.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("users.empty")}
            </Typography>
          ) : (
            users.map((u: any, index: number) => {
              const fullName = [u.name, u.surname]
                .filter(Boolean)
                .join(" ")
                .trim();
              const subParts = [
                u.email,
                groupId ? `${t("users.groupId")}: ${groupId}` : undefined,
              ]
                .filter(Boolean)
                .join(" | ");
              return (
                <ListItemCardCst
                  key={u.id ?? u.user_id ?? index}
                  title={fullName || u.externalId || String(u.id ?? "")}
                  subtitle={{ id: u.id ?? u.user_id, activityType: subParts }}
                  badges={[]}
                  actions={[
                    {
                      label: t("details"),
                      variant: "outlined",
                      onClick: () => goToUserDetail(u),
                    },
                  ]}
                />
              );
            })
          )}

          {usersPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={usersPage}
                pageSize={PAGE_SIZE}
                totalItems={usersTotal}
                onPageChange={setUsersPage}
              />
            </Box>
          )}
        </Box>

        {/* Oggetti formativi associati */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("learningObjects.title")}
            action={
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                }}
              >
                {t("downloadCsv")}
              </Button>
            }
          />

          <TabsCst tabs={loTabs} activeKey={loTab} onChange={setLoTab} />

          <Box sx={searchBoxSx}>
            <InputCst
              id="dyn-group-lo-search"
              label={t("search")}
              value={loSearch}
              onChange={(e: any) => {
                setLoSearch(e.target.value);
                setLoPage(1);
              }}
              endAdornment={
                <Search size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>

          {loadingLo ? (
            <CircularProgress />
          ) : filteredLos.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("learningObjects.empty")}
            </Typography>
          ) : (
            filteredLos.map((raw: any, index: number) => {
              const entity = raw.lo || raw;
              const entityType = mapAggregatedType(raw.type || entity.type);
              const cardProps = mapEntityToCard(entity, entityType, {
                t: tCatalog,
                onDetails: () => {},
              });
              return (
                <ListItemCardCst
                  key={entity.id ?? index}
                  {...cardProps}
                  actions={[]}
                />
              );
            })
          )}

          {loPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={loPage}
                pageSize={PAGE_SIZE}
                totalItems={loTotal}
                onPageChange={setLoPage}
              />
            </Box>
          )}
        </Box>
      </Box>
    </AnimationFadeIn>
  );
};

export default DynamicGroupDetail;
